import { withProtectedResource } from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import { Stack } from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { GetChargePointsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/get-charge-points-paginated.model';
import { safeHasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { transformPaginatedResponse } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transform-api-response';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import {
  OnChangeFn,
  SortingState,
  functionalUpdate,
} from '@tanstack/react-table';
import { useMemo } from 'react';

import { permissions } from '@app/core/authentication/authentication.slice';
import { getState } from '@app/core/helper/get-state';
import { useAppSelector } from '@app/core/hooks';
import { useCpmsBoGetChargePointsPaginatedQuery } from '@app/core/service/cpms-bo-charge-point.service';
import { CHARGE_POINT_LIST_SORT_FIELD } from '@app/feature/charge-point/charge-point-list/charge-point-list.config';

import {
  LocationChargePointListUpdateContextProvider,
  useLocationChargePointListUpdateContext,
  useLocationContext,
} from '../../location.context';
import * as css from './location-charge-point-list-update-form-step-1.module.scss';
import { LocationChargePointListUpdateFormStep1ChargePointView } from './location-charge-point-list-update-form-step-1.view';

export const LocationChargePointListUpdateFormStep1ChargePointController =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    const {
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList,
    } = useLocationChargePointListUpdateContext();

    // USER PERMISSIONS ////////////////////////////////////////////////////////

    const userPermissions = useAppSelector(permissions);

    // ACTIONS /////////////////////////////////////////////////////////////////

    // Nothing to do

    // DATA FETCHING ///////////////////////////////////////////////////////////

    const queryState = useCpmsBoGetChargePointsPaginatedQuery(
      getPaginatedQueryParameters,
    );

    const paginationState = useMemo(
      () =>
        getState({
          isLoading: queryState.isLoading,
          isError: queryState.isError,
          isFetching: queryState.isFetching,
        }),
      [queryState.isLoading, queryState.isError, queryState.isFetching],
    );

    // PAGE TITLE //////////////////////////////////////////////////////////////

    // Nothing to do

    // PAGE ACTIONS ////////////////////////////////////////////////////////////

    // Nothing to do

    // PAGE BREADCRUMB /////////////////////////////////////////////////////////

    // Nothing to do

    // HANDLERS ////////////////////////////////////////////////////////////////

    const handlePaginationPageChange = (paginationPageNext: number) => {
      setGetPaginatedQueryParameters((current) => ({
        ...current,
        offset: (paginationPageNext - 1) * current.limit,
      }));
    };

    const handlePaginationLimitChange = (paginationLimitNext: number) => {
      setGetPaginatedQueryParameters((current) => ({
        ...current,
        limit: paginationLimitNext,
        offset: 0,
      }));
    };

    const handlePaginationSortingChange: OnChangeFn<SortingState> = (
      sortingState,
    ) => {
      setGetPaginatedQueryParameters((current) => {
        const paginationSorting0 = functionalUpdate(sortingState, [
          {
            desc: current.sortOrder === -1,
            id: current.sortField ?? CHARGE_POINT_LIST_SORT_FIELD,
          },
        ]).at(0);

        return {
          ...current,
          sortField:
            paginationSorting0?.id as GetChargePointsPaginatedModel['sortField'],
          sortOrder: paginationSorting0?.desc ? -1 : 1,
        };
      });
    };

    // RENDER //////////////////////////////////////////////////////////////////

    const {
      Component: LocationChargePointListUpdateFormStep1ChargePointViewProtected,
    } = withProtectedResource(
      LocationChargePointListUpdateFormStep1ChargePointView,
      queryState,
      {
        isAuthorized: () =>
          safeHasAccessTo(userPermissions, {
            resourceName: Resource.CHARGE_POINTS,
            action: ActionsByResource.CHARGE_POINTS.READ,
          }),
        transformDataToResource: transformPaginatedResponse,
      },
    )({
      paginationLimit: getPaginatedQueryParameters.limit,
      paginationLimitList: getPaginatedLimitList,
      paginationState,
      paginationSorting: [
        {
          desc: getPaginatedQueryParameters.sortOrder === -1,
          id:
            getPaginatedQueryParameters.sortField ??
            CHARGE_POINT_LIST_SORT_FIELD,
        },
      ],
      onPaginationLimitChange: handlePaginationLimitChange,
      onPaginationPageChange: handlePaginationPageChange,
      onPaginationSortingChange: handlePaginationSortingChange,
    });

    return (
      <Stack className={css.tableContainer}>
        {LocationChargePointListUpdateFormStep1ChargePointViewProtected}
      </Stack>
    );
  };

export const LocationChargePointListUpdateFormStep1Controller =
  (): React.JSX.Element => {
    const location = useLocationContext();

    return (
      <Stack flex={1} className={css.listContainer} direction="column">
        <LocationChargePointListUpdateContextProvider data={{ location }}>
          <LocationChargePointListUpdateFormStep1ChargePointController />
        </LocationChargePointListUpdateContextProvider>
      </Stack>
    );
  };
